/**
 * In-memory scroll position per conversation.
 *
 * The chat panel is a single mount that swaps its underlying store as the user
 * switches tabs (see ChatbotContainer) — the message list is never remounted,
 * so its scroll offset would otherwise carry over from the previous tab. We
 * record where the user was in each conversation and restore it on switch.
 *
 * Ephemeral by design: a pixel offset is only meaningful while the rendered
 * content is unchanged, so it must never be persisted across reloads (the
 * messages that produced that offset may not even be loaded yet). We also track
 * `atBottom` so a conversation that received more content while backgrounded
 * (or that the user was following) is restored to the true bottom, not a stale
 * pixel that now sits mid-history.
 */

export interface ConversationScrollPosition {
  scrollTop: number;
  atBottom: boolean;
}

const scrollPositions = new Map<string, ConversationScrollPosition>();

export const saveConversationScrollPosition = (
  conversationId: string,
  position: ConversationScrollPosition,
): void => {
  scrollPositions.set(conversationId, position);
};

export const getConversationScrollPosition = (
  conversationId: string,
): ConversationScrollPosition | undefined => scrollPositions.get(conversationId);

export const clearConversationScrollPosition = (conversationId: string): void => {
  scrollPositions.delete(conversationId);
};
